import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import Pagination from "@/components/feature/Pagination";
import FilterBar, { type FilterValue } from "@/pages/category/components/FilterBar";
import { EmptyState, LoadingState } from "@/components/base/StateViews";
import {
  applyFilters,
  getRegions,
  getYears,
  paginate,
  films,
  totalPages,
} from "@/lib/filmData";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import type { FilmType } from "@/types/film";
import NotFound from "@/pages/NotFound";

const DEFAULTS: FilterValue = { year: "all", region: "all", rating: "all", sort: "latest" };

const TYPE_META: Record<FilmType, { label: string; description: string }> = {
  movies: {
    label: "电影",
    description: "收录院线佳作与经典影片，按年份、地区与评分筛选，快速找到你想看的电影。",
  },
  tv: {
    label: "电视剧",
    description: "汇聚热播剧集与口碑长篇，支持按年份、地区与评分筛选，追剧不再迷路。",
  },
};

function isFilmType(value?: string): value is FilmType {
  return value === "movies" || value === "tv";
}

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const validType = isFilmType(type);
  const filmType: FilmType = validType ? (type as FilmType) : "movies";

  const [filters, setFilters] = useState<FilterValue>(DEFAULTS);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters(DEFAULTS);
    setPage(1);
  }, [filmType]);

  const meta = TYPE_META[filmType];
  const years = useMemo(() => getYears(filmType), [filmType]);
  const regions = useMemo(() => getRegions(filmType), [filmType]);

  const results = useMemo(
    () =>
      applyFilters(
        {
          type: filmType,
          year: filters.year,
          region: filters.region,
          rating: filters.rating,
          sort: filters.sort as "latest" | "popularity" | "rating",
        },
        films,
      ),
    [filmType, filters],
  );

  const pages = totalPages(results.length);
  const currentPage = Math.min(page, pages);
  const visible = paginate(results, currentPage);
  const loading = useSimulatedLoading([filmType, filters, currentPage], 400);

  useDocumentMeta({
    title: validType ? `${meta.label}分类 - 新金瓶梅` : "影视分类 - 新金瓶梅",
    description: validType
      ? `${meta.label}分类页面，${meta.description}`
      : "浏览新金瓶梅的电影与电视剧分类内容。",
    keywords: `新金瓶梅,${meta.label},新金瓶梅${meta.label},影视分类,剧情介绍`,
  });

  if (!validType) {
    return <NotFound />;
  }

  const handleChange = (key: keyof FilterValue, next: string) => {
    setFilters((prev) => ({ ...prev, [key]: next }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(DEFAULTS);
    setPage(1);
  };

  const handlePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  return (
    <div className="page-shell pb-8 pt-24 md:pt-28">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视分类", to: "/category/movies" },
          { label: `${meta.label}频道` },
        ]}
      />

      <header className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
            {meta.label}频道
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">{meta.description}</p>
        </div>
        <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
          <Link
            to="/category/movies"
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filmType === "movies"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            电影
          </Link>
          <Link
            to="/category/tv"
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filmType === "tv"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            电视剧
          </Link>
        </div>
      </header>

      <div className="mt-8">
        <FilterBar
          years={years}
          regions={regions}
          value={filters}
          onChange={handleChange}
          onReset={handleReset}
        />
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-foreground-900">{results.length}</span> 部{meta.label}作品
        </p>
      </div>

      <div className="mt-5">
        {loading ? (
          <LoadingState label={`正在加载${meta.label}作品…`} />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="ri-film-line"
            title="未找到符合条件的影视作品"
            description="试试放宽筛选条件，或直接重置筛选查看全部内容。"
            actionLabel="重置筛选"
            onAction={handleReset}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {visible.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        )}
      </div>

      {!loading && visible.length > 0 && (
        <Pagination page={currentPage} total={pages} onChange={handlePage} />
      )}
    </div>
  );
}